import { SectionTitle } from '@elotech/components';
import { ArquivoComponent } from 'common/components';
import { ArquivoService } from 'common/service';
import { CrudRoleBase, FilePathEnum } from 'common/type/enum';
import { ReactNode, RefAttributes, forwardRef, useMemo } from 'react';

import { useCrudRole } from '../../hooks';
import { ArquivoEntity } from '../../type';
import { ArquivoComponentRef } from './ArquivoComponent';

export type ArquivoRef = ArquivoComponentRef;

type BaseProps = {
  id?: string;
  title?: string;
  baseUrl: string;
  byTipo?: string;
  resource: string;
  loading?: boolean;
  canUpload?: boolean;
  canDelete?: boolean;
  showTitle?: boolean;
  filePath: FilePathEnum;
  maxFileNameLength?: number;
  maxFileCount?: number;
  onVisualizarArquivoHtml?: (
    template: string,
    fileName: string
  ) => Promise<string | undefined | void>;
  extraButton?: (file: ArquivoEntity) => any;
  labels?: (file: ArquivoEntity) => any;
  showArquivoProps?: boolean;
  children?: ReactNode;
  getExtraCaption?(file: ArquivoEntity): any;
  afterSave?: (arquivoEntity: ArquivoEntity) => void;
  afterDelete?: () => void;
};

type PropsRole<ROLE extends string | undefined = undefined> =
  ROLE extends string
    ? { crudRole: CrudRoleBase<ROLE> }
    : { crudRole?: undefined };

type Props<Role extends string | undefined = undefined> = BaseProps &
  PropsRole<Role>;

type ArquivoType = <ROLE extends string | undefined = undefined>(
  props: Props<ROLE> & RefAttributes<ArquivoComponentRef>
) => React.ReactElement | null;

const Arquivo: ArquivoType = forwardRef(
  <ROLE extends string | undefined = undefined>(
    props: Props<ROLE>,
    ref: React.Ref<ArquivoRef>
  ) => {
    const {
      baseUrl,
      loading,
      resource,
      canUpload = true,
      canDelete = true,
      showTitle = true,
      title = 'Arquivos',
      extraButton,
      labels,
      afterSave,
      afterDelete,
      crudRole,
      showArquivoProps,
      ...rest
    } = props;

    const crudPermissions = useCrudRole<ROLE>(crudRole);
    const hasWritePermission = crudPermissions.update || crudPermissions.create;

    const service = useMemo(
      () => new ArquivoService(baseUrl, resource),
      [baseUrl, resource]
    );

    if (!crudPermissions.read) {
      return null;
    }

    return (
      <>
        {showTitle && <SectionTitle>{title}</SectionTitle>}
        <ArquivoComponent
          service={service}
          isLoading={loading}
          canUpload={crudPermissions.create && canUpload}
          canDelete={crudPermissions.delete && canDelete}
          extraButton={hasWritePermission ? extraButton : undefined}
          showArquivoProps={hasWritePermission && showArquivoProps}
          labels={labels}
          maxFileCount={props.maxFileCount}
          ref={ref}
          afterSave={afterSave}
          afterDelete={afterDelete}
          {...rest}
        />
      </>
    );
  }
);

export default Arquivo;
